import React from 'react';
import { Dispatch } from 'redux';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import t from '../../../i18n/t';
import { Column, Row } from '../../common/layoutUtils';
import { LargeText, MiniText, RegularText, SmallText } from '../../../utils/typography';
import Button from '../../common/buttons/Button';
import { RootState } from '@state/types';
import { closeCategoryTimeDialog } from '@state/task/actions';
import { setCategoryMinutes } from '@state/course/actions';
import { formatMinutes } from '@components/course/taskEstimator/utils';
import { DEFAULT_TASK_CATEGORY_EMOJI } from '@utils/constants/task';
import { CategorySuggestion, shortCategoryName } from '@utils/categoryTime';
import { getAllActivities, getAllCourses } from '@state/calendar/selectors';
import { EmojiPreview } from '@components/common/emoji/EmojiPreview';
import { CourseRow, TitleWrapper } from '@components/task/TaskCard.styles';
import colors from '@utils/colors';

/** Answers the category question: Yes makes the time the category's default and re-estimates its tasks still to do. */
export const answerCategoryTime = (dispatch: Dispatch, data: CategorySuggestion, yes: boolean) => {
  if (yes) {
    dispatch(
      setCategoryMinutes.request({
        courseId: data.courseId,
        categoryId: data.categoryId,
        minutes: data.newMinutes,
        applyToUnfinished: true
      })
    );
  }
  dispatch(closeCategoryTimeDialog());
};

type Props = {
  data: CategorySuggestion;
  // The task's card at the top, as the overlay shows it; a session's popup has the task's name above already.
  withTask?: boolean;
  onAnswer: (yes: boolean) => void;
};

/**
 * Asked once a task is completed (Petr, 2026-10-28/29): use the time it took for its category's tasks still to do, and
 * for its tasks from now on. With a category time to go by - its default, else the one most of its tasks share - it's
 * asked when the task took 20% or more longer or shorter, and shows the two side by side; with none (most students'
 * categories before defaults), it's asked whatever the task took. Built as the reading's "Time per page" question is.
 */
const CategoryTimeQuestion = ({ data, withTask, onAnswer }: Props) => {
  // The task's course colour, for its card.
  const colorHex = useSelector(
    (state: RootState) =>
      [...getAllCourses(state), ...getAllActivities(state)].find(course => course.id === data.courseId)?.colorHex
  );
  const time = formatMinutes(data.newMinutes)!;
  const current = data.currentMinutes != null ? formatMinutes(data.currentMinutes)! : null;
  const emoji = data.emoji || DEFAULT_TASK_CATEGORY_EMOJI;
  const title =
    data.currentMinutes == null
      ? t.CATEGORY_TIME_TOOK(time)
      : data.newMinutes > data.currentMinutes
      ? t.CATEGORY_TIME_LONGER
      : t.CATEGORY_TIME_SHORTER;

  return (
    <Content>
      {/* The task as its card shows it in the sidebar: course, category, name (Petr, 2026-10-29). */}
      {withTask && (
        <TaskCard $color={colorHex || colors.randomTaskColor}>
          <CourseRow>
            <SmallText color={colorHex}>{data.courseName}</SmallText>
            <MiniText>
              <EmojiPreview emoji={emoji} />
              {data.categoryName}
            </MiniText>
          </CourseRow>
          <TitleWrapper title={data.taskTitle}>{data.taskTitle}</TitleWrapper>
        </TaskCard>
      )}
      <Title strong>{title}</Title>
      {current && (
        <Compare>
          <Time>
            <TimeLabel>{t.CATEGORY_TIME_ESTIMATED}</TimeLabel>
            <TimeValue strong>{current}</TimeValue>
          </Time>
          <Arrow>→</Arrow>
          <Time $new>
            <TimeLabel>{t.CATEGORY_TIME_ACTUAL}</TimeLabel>
            <TimeValue strong>{time}</TimeValue>
          </Time>
        </Compare>
      )}
      {/* Only about the tasks remaining; "Yes" also makes it the category's default, which the toast after says. */}
      <Question>
        {t.CATEGORY_TIME_QUESTION_START(time, data.remainingCount)}{' '}
        <CategoryName title={data.categoryName}>
          <EmojiPreview emoji={emoji} />
          {shortCategoryName(data.categoryName)}
        </CategoryName>
        ?
      </Question>
      <Button filled onClick={() => onAnswer(true)}>
        {t.CATEGORY_TIME_YES(time)}
      </Button>
      <Button className={'custom-button'} withoutBorder onClick={() => onAnswer(false)}>
        {t.CATEGORY_TIME_NO}
      </Button>
    </Content>
  );
};

export default CategoryTimeQuestion;

const Content = styled(Column)`
  position: relative;
  width: 100%;
  .custom-button {
    margin-top: 10px;
    background: ${props => `${props.theme.primary}1F`};
  }
`;

const TaskCard = styled(Column)<{ $color: string }>`
  align-items: stretch;
  padding: 7px 10px 8px;
  margin-bottom: 14px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-left: 4px solid ${({ $color }) => $color};
  border-radius: 8px;
  text-align: left;

  ${CourseRow} {
    color: ${({ theme }) => theme.textColor};
  }
  .emoji-mart-emoji {
    margin-right: 3px;
  }
`;

const Title = styled(LargeText)`
  margin-top: 0;
  text-align: center;
  display: block;
  margin-bottom: 14px;
  overflow-wrap: anywhere;
`;

const Compare = styled(Row)`
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const Time = styled(Column)<{ $new?: boolean }>`
  align-items: center;
  gap: 2px;
  min-width: 84px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${({ theme, $new }) =>
    $new ? `color-mix(in srgb, ${theme.primary} 14%, ${theme.background})` : theme.borderColor};
  color: ${({ theme, $new }) => ($new ? theme.primary : theme.textStrongColor)};
`;

const TimeLabel = styled(SmallText)`
  color: inherit;
  opacity: 0.8;
`;

const TimeValue = styled(LargeText)`
  color: inherit;
  margin: 0;
`;

const Arrow = styled(RegularText)`
  color: ${({ theme }) => theme.textColor};
`;

const Question = styled(RegularText)`
  text-align: center;
  margin-bottom: 20px;
`;

// The category, as its tag shows it: emoji and name, in bold.
const CategoryName = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  font-weight: 700;
  color: ${({ theme }) => theme.textStrongColor};
  white-space: nowrap;

  .emoji-mart-emoji {
    align-self: center;
  }
`;
